import { Dropdown, MenuProps } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout as logoutAction } from '../../redux/auth/actions';
import { selectAuth } from '../../redux/auth/selectors';
import { useCurrentUser } from '../hooks/useCurrentUser';
import ResourceIcon from './resource/ResourceIcon';
import './UserMenu.css';

interface UserMenuOptions {
  /** Items placed just above "Log out", e.g. the platform console's way back. */
  extraItems?: MenuProps['items'];
  /** The platform console hides its own entry; everywhere else it shows for platform accounts. */
  hidePlatformLink?: boolean;
}

const initialsFor = (name: string) =>
  name.split(/[\s@]+/).filter(Boolean).map((part) => part[0]).slice(0, 2).join('').toUpperCase();

/**
 * The account menu's items, built once so the workspace app, the profile page and the
 * platform console show the same menu rather than three hand-made copies.
 */
export const useUserMenuItems = ({
  extraItems = [],
  hidePlatformLink = false
}: UserMenuOptions = {}): MenuProps['items'] => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { current } = useSelector(selectAuth);
  const { canAccessPlatformAdmin, isPlatformOwner } = useCurrentUser();

  const email: string | undefined = current?.email;
  const name = `${current?.first_name || ''} ${current?.last_name || ''}`.trim() || email;

  const items: MenuProps['items'] = [
    {
      key: 'profile-head',
      className: 'sw-account-identity-item',
      label: (
        <div className="sw-account-identity">
          <span className="sw-account-avatar">{initialsFor(name ?? '')}</span>
          <div className="sw-account-details">
            <strong>{name}</strong>
            <span>{email}</span>
            {canAccessPlatformAdmin && (
              <span className="sw-account-role">
                <ResourceIcon name="shield" size={12} />
                {isPlatformOwner ? 'Platform owner' : 'Support administrator'}
              </span>
            )}
          </div>
        </div>
      )
    }, { type: 'divider' }, {
      key: 'profile',
      icon: <ResourceIcon name="users" />,
      label: 'My profile',
      onClick: () => navigate('/profile')
    }
  ];
  // Left out rather than flagged: antd menu items have no `hidden` prop.
  if (canAccessPlatformAdmin && !hidePlatformLink) {
    items.push({
      key: 'platform-administration',
      icon: <ResourceIcon name="building" />,
      label: 'Administration',
      onClick: () => navigate('/admin')
    });
  }
  items.push({ type: 'divider' }, ...(extraItems ?? []), {
    key: 'logout',
    danger: true,
    icon: <ResourceIcon name="logout" />,
    label: 'Log out',
    onClick: () => dispatch<any>(logoutAction()).then(() => navigate('/login'))
  });
  return items;
};

/** The account button that opens the menu. */
export const UserMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const { current } = useSelector(selectAuth);
  const name =
    `${current?.first_name || ''} ${current?.last_name || ''}`.trim() || current?.email || 'My account';
  return (
    <button 
      {...props} 
      ref={ref} 
      className="sw-account-trigger" 
      aria-label="My account"
    >
      <span className="sw-account-avatar">{initialsFor(name)}</span>
      <span className="sw-account-name">{name}</span>
      <ResourceIcon name="down" size={14} />
    </button>
  );
});
UserMenuTrigger.displayName = 'UserMenuTrigger';

/** Trigger and menu together, for layouts that don't render their own header avatar. */
const UserMenu: React.FC<UserMenuOptions> = (options) => {
  const items = useUserMenuItems(options);
  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="sw-account-dropdown"
      autoAdjustOverflow
    >
      <UserMenuTrigger />
    </Dropdown>
  );
};

export default UserMenu;
